import React, { useState } from 'react';
import Button from './Button';
import './ContactForm.css';

interface ContactFormProps {
  showWhatsApp?: boolean;
  whatsAppNumber?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({
  showWhatsApp = false,
  whatsAppNumber = '255765210248'
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [issueType, setIssueType] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, issueType, message }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Message sent successfully! We\'ll get back to you soon.');
        setName('');
        setEmail('');
        setIssueType('');
        setMessage('');
      } else {
        setError(data.message || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = () => {
    const formattedNumber = whatsAppNumber.replace(/[^0-9]/g, '');
    const whatsappMessage = `Hello, I need support with NuruSubs.

Name: ${name || 'Not provided'}
Email: ${email || 'Not provided'}
Issue Type: ${issueType || 'Not specified'}

Description:
${message || 'No description provided'}`;
    
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/${formattedNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="contact-form-container">
      <div className="contact-form-wrapper">
        <form onSubmit={handleSubmit} className="contact-form">
          <h2 className="contact-form-title">Send us a Message</h2>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="form-group">
            <label htmlFor="contact-name">Name</label>
            <input
              type="text"
              id="contact-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Your name"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact-email">Email</label>
            <input
              type="email"
              id="contact-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact-issue-type">Type of Issue</label>
            <select
              id="contact-issue-type"
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              required
              className="form-input"
            >
              <option value="">Select an issue</option>
              <option value="Technical Issue">Technical Issue</option>
              <option value="Account Problem">Account Problem</option>
              <option value="Billing Question">Billing Question</option>
              <option value="Feature Request">Feature Request</option>
              <option value="Translation Quality">Translation Quality</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="contact-message">Description</label>
            <textarea
              id="contact-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              placeholder="How can we help?"
              className="form-input contact-textarea"
              rows={5}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            className="contact-submit-button"
          >
            {loading ? 'Sending...' : 'Send Message'}
          </Button>
        </form>

        {showWhatsApp && (
          <div className="contact-info-section">
            <div className="contact-info-card">
              <h3 className="contact-info-title">Contact Information</h3>

              <div className="contact-info-item">
                <span className="contact-icon">✉️</span>
                <div>
                  <strong>Email</strong>
                  <p>info@nurutechnologies.com</p>
                </div>
              </div>

              <div className="contact-info-item whatsapp-section">
                <span className="contact-icon whatsapp-icon">💬</span>
                <div>
                  <strong>WhatsApp</strong>
                  <p>Click the button to chat with us</p>
                  <button
                    type="button"
                    onClick={handleWhatsAppClick}
                    className="whatsapp-button"
                  >
                    <span className="whatsapp-logo">WhatsApp</span>
                  </button>
                </div>
              </div>

              <div className="business-hours">
                <strong>Business Hours</strong>
                <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                <p>Saturday: 9:00 AM - 1:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactForm;
